import React, { useEffect, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { circuitBreaker, CircuitState } from '../api/circuitBreaker';

const BackendStatusNotification: React.FC = () => {
  const [circuitState, setCircuitState] = useState<CircuitState>('CLOSED');
  const [retryCount, setRetryCount] = useState<number>(0);
  const [nextRetryTime, setNextRetryTime] = useState<number | null>(null);
  const [countdown, setCountdown] = useState<number>(0);

  useEffect(() => {
    // Subscribe to circuit breaker state changes
    const unsubscribe = circuitBreaker.onStateChange((state, count, nextRetry) => {
      setCircuitState(state);
      setRetryCount(count);
      setNextRetryTime(nextRetry || null);
    });

    // Initial state
    setCircuitState(circuitBreaker.getState());
    setRetryCount(circuitBreaker.getRetryCount());
    setNextRetryTime(circuitBreaker.getNextRetryTime());

    return () => {
      unsubscribe();
    };
  }, []);

  // Update countdown every second
  useEffect(() => {
    if (circuitState !== 'OPEN' || !nextRetryTime) {
      setCountdown(0);
      return;
    }

    const updateCountdown = () => {
      const remaining = Math.max(0, Math.ceil((nextRetryTime - Date.now()) / 1000));
      setCountdown(remaining);
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [circuitState, nextRetryTime]);

  // Don't show anything if circuit is closed
  if (circuitState === 'CLOSED') {
    return null;
  }

  const handleRetryNow = () => {
    circuitBreaker.retryNow();
  };

  return (
    <Alert
      variant="warning"
      className="position-fixed top-0 start-50 translate-middle-x mt-3 shadow-lg"
      style={{ zIndex: 9999, minWidth: '400px', maxWidth: '600px' }}
    >
      <div className="d-flex align-items-center">
        <i className="bi bi-exclamation-triangle-fill me-3 fs-4"></i>
        <div className="flex-grow-1">
          <strong>Backend Unreachable</strong>
          <div className="small mt-1">
            {countdown > 1 ? (
              <>Retrying in {countdown} seconds... (attempt {retryCount})</>
            ) : countdown === 1 ? (
              <>Retrying in 1 second... (attempt {retryCount})</>
            ) : (
              <>Connecting... (attempt {retryCount})</>
            )}
          </div>
        </div>
        <Button
          variant="outline-warning"
          size="sm"
          onClick={handleRetryNow}
          className="ms-3"
        >
          Retry Now
        </Button>
      </div>
    </Alert>
  );
};

export default BackendStatusNotification;
